import { ENABLED_LOCALES, type LocaleDef } from "@/lib/i18n/locales";

/**
 * Region grouping for the language switcher.
 *
 * Purely presentational: it makes a 20+ locale list scannable by grouping the
 * endonyms under the region a reader is most likely to look in. Geo/SEO
 * targeting itself lives in `geoTargets.ts`.
 */
export const LOCALE_REGIONS: Record<string, string> = {
  en: "Global",
  es: "Americas & Europe",
  pt: "Americas & Europe",
  fr: "Europe & Africa",
  de: "Europe",
  it: "Europe",
  nl: "Europe",
  pl: "Europe",
  el: "Europe",
  sv: "Europe",
  ru: "Europe & Central Asia",
  tr: "Europe & Middle East",
  ar: "Middle East & North Africa",
  am: "Africa",
  hi: "South Asia",
  id: "Southeast Asia",
  ms: "Southeast Asia",
  fil: "Southeast Asia",
  vi: "Southeast Asia",
  th: "Southeast Asia",
  zh: "East Asia",
  "zh-TW": "East Asia",
  ja: "East Asia",
  ko: "East Asia",
};

const REGION_ORDER = [
  "Global",
  "Europe",
  "Europe & Africa",
  "Europe & Middle East",
  "Europe & Central Asia",
  "Americas & Europe",
  "Middle East & North Africa",
  "Africa",
  "South Asia",
  "Southeast Asia",
  "East Asia",
  "Other",
];

export interface LocaleGroup {
  region: string;
  locales: LocaleDef[];
}

/** Enabled locales grouped by region, optionally filtered by a search query. */
export function groupedLocales(query = ""): LocaleGroup[] {
  const q = query.trim().toLowerCase();
  const matches = (l: LocaleDef) =>
    !q ||
    l.code.toLowerCase().includes(q) ||
    l.englishName.toLowerCase().includes(q) ||
    l.nativeName.toLowerCase().includes(q) ||
    (LOCALE_REGIONS[l.code] ?? "").toLowerCase().includes(q);

  const buckets = new Map<string, LocaleDef[]>();
  for (const l of ENABLED_LOCALES) {
    if (!matches(l)) continue;
    const region = LOCALE_REGIONS[l.code] ?? "Other";
    const list = buckets.get(region) ?? [];
    list.push(l);
    buckets.set(region, list);
  }

  return REGION_ORDER.filter((r) => buckets.has(r)).map((region) => ({
    region,
    locales: (buckets.get(region) ?? []).sort((a, b) => a.englishName.localeCompare(b.englishName)),
  }));
}
